/**
 * Turning a parsed markdown document into elements.
 *
 * The counterpart to `domain/markdown.ts`, and the reason that module hands back a tree
 * rather than a string of HTML. **Nothing here writes markup.** Every element is made
 * with `createElement` and every piece of text with `textContent`, so a `<script>` in the
 * source file arrives as the eleven characters somebody typed and is drawn as those
 * eleven characters. There is no `innerHTML` to be careful around, which means there is
 * no sanitiser to keep up to date and no way for a future edit here to open a hole that
 * was not there before.
 *
 * The destinations of links and images are the one place text still becomes behaviour,
 * and those were checked by `safeUrl` before they got this far.
 */

import type { Block, Inline } from '../domain/markdown.js';

export function renderMarkdown(blocks: readonly Block[]): readonly Node[] {
  return blocks.map(renderBlock);
}

function renderBlock(block: Block): Node {
  switch (block.kind) {
    case 'heading': {
      // Levels start at h2: the page's own <h1> is the site title, and a document dropped
      // into a panel on that page is beneath it however its author numbered their
      // headings. h6 is the floor, so a deeply nested document cannot run off the end.
      const heading = document.createElement(
        `h${Math.min(block.level + 1, 6).toString()}`,
      );
      heading.append(...renderInline(block.content));
      return heading;
    }

    case 'paragraph': {
      const paragraph = document.createElement('p');
      paragraph.append(...renderInline(block.content));
      return paragraph;
    }

    case 'code': {
      const pre = document.createElement('pre');
      const code = document.createElement('code');
      code.textContent = block.text;
      // Named the way every highlighter and stylesheet expects, so that a document
      // written elsewhere keeps whatever its author meant by it.
      if (block.language !== undefined) code.className = `language-${block.language}`;
      pre.append(code);
      return pre;
    }

    case 'quote': {
      const quote = document.createElement('blockquote');
      quote.append(...renderMarkdown(block.blocks));
      return quote;
    }

    case 'list': {
      const list = block.ordered
        ? document.createElement('ol')
        : document.createElement('ul');

      // A list that starts at three is numbered from three, which is a thing people do
      // when a page walks through steps in more than one piece.
      if (list instanceof HTMLOListElement && block.start !== 1) list.start = block.start;

      for (const item of block.items) {
        const entry = document.createElement('li');
        const only = item.length === 1 ? item[0] : undefined;

        // A single paragraph goes in without its <p>. That is what makes an ordinary
        // list tight — one line per item — while an item that grew into several blocks
        // keeps the spacing it needs. The stylesheet gets to stay out of it.
        if (only?.kind === 'paragraph') entry.append(...renderInline(only.content));
        else entry.append(...renderMarkdown(item));

        list.append(entry);
      }

      return list;
    }

    case 'rule':
      return document.createElement('hr');
  }
}

function renderInline(content: readonly Inline[]): readonly Node[] {
  return content.map(renderNode);
}

function renderNode(node: Inline): Node {
  switch (node.kind) {
    case 'text':
      return document.createTextNode(node.text);

    case 'code': {
      const code = document.createElement('code');
      code.textContent = node.text;
      return code;
    }

    case 'emphasis': {
      const emphasis = document.createElement('em');
      emphasis.append(...renderInline(node.content));
      return emphasis;
    }

    case 'strong': {
      const strong = document.createElement('strong');
      strong.append(...renderInline(node.content));
      return strong;
    }

    case 'link': {
      const link = document.createElement('a');
      link.href = node.href;
      if (node.title !== undefined) link.title = node.title;

      // Somewhere else entirely opens in its own tab: this panel sits over a viewer
      // holding a scene and a library, and following a link out of it would take those
      // with it. A relative link stays in place, since it is part of the same deployment
      // and its author presumably meant to navigate.
      if (/^[a-z][a-z0-9+.-]*:/i.test(node.href)) {
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
      }

      link.append(...renderInline(node.content));
      return link;
    }

    case 'image': {
      const image = document.createElement('img');
      image.src = node.src;
      image.alt = node.alt;
      if (node.title !== undefined) image.title = node.title;
      // Below the fold of a long page, an image nobody scrolls to is never fetched.
      image.loading = 'lazy';
      return image;
    }

    case 'break':
      return document.createElement('br');
  }
}
